/**
 * BillingEncounterRow Component
 *
 * Dense row displaying a billing encounter with working claim data.
 * Renders as a CSS Grid row in table layouts or as a stacked card on mobile.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '@components/primitives/Avatar';
import {
  AddCircleOutline as CreateClaimIcon,
  Description as NoteIcon,
  EventNote as EncounterIcon,
  MoreVert as MoreVertIcon,
  OpenInNew as ViewClaimIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Videocam as VideoIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { OrgBillingEncounterResponse } from '@store/generated/billing';

import { getReadyToBillColor, getReadyToBillLabel } from '../../EncounterDetail/utils';

import type { TableLayoutMode } from './types';
import { getGridTemplateColumns, GRID_GAP } from './types';

// ---------------------------------------------------------------------------
// Working billing claim type (matches WorkingBillingClaimResponse shape)
// ---------------------------------------------------------------------------

interface WorkingClaimLineItem {
  code_id?: string | null;
  charge_cents?: number | null;
  modifiers?: string[];
  units?: number | null;
  source?: 'provider_selected' | 'ai_recommended' | null;
}

interface WorkingBillingClaim {
  line_items?: WorkingClaimLineItem[];
  diagnosis_codes?: string[];
  place_of_service?: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getNameParts(fullName: string | null | undefined): { firstName: string; lastName: string } {
  if (!fullName) return { firstName: '', lastName: '' };
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function getPatientDisplayName(encounter: OrgBillingEncounterResponse): string {
  const info = encounter.patient_info as { first_name?: string; last_name?: string; preferred_name?: string } | null;
  if (info?.preferred_name) return info.preferred_name;
  if (info?.first_name) {
    return [info.first_name, info.last_name].filter(Boolean).join(' ');
  }
  return encounter.patient_name || 'Patient';
}

function getPatientNameParts(encounter: OrgBillingEncounterResponse): { firstName: string; lastName: string } {
  const info = encounter.patient_info as { first_name?: string; last_name?: string } | null;
  if (info?.first_name || info?.last_name) {
    return { firstName: info?.first_name || '', lastName: info?.last_name || '' };
  }
  return getNameParts(encounter.patient_name);
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const formatTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

const formatCents = (cents: number): string => {
  const dollars = cents / 100;
  return `$${dollars.toFixed(0)}`;
};

const getModalityIcon = (modality: string | null | undefined) => {
  switch (modality) {
    case 'video':
      return <VideoIcon sx={{ fontSize: 16 }} />;
    case 'audio':
      return <PhoneIcon sx={{ fontSize: 16 }} />;
    case 'in_person':
      return <PersonIcon sx={{ fontSize: 16 }} />;
    default:
      return <EncounterIcon sx={{ fontSize: 16 }} />;
  }
};

const getClaimStatusColor = (status: string | null | undefined): 'default' | 'success' | 'warning' | 'info' | 'error' | 'primary' => {
  switch (status) {
    case 'accepted':
    case 'paid':
      return 'success';
    case 'submitted':
    case 'acknowledged':
      return 'info';
    case 'draft':
      return 'default';
    case 'rejected':
    case 'failed':
    case 'denied':
      return 'error';
    case 'processing':
    case 'scrubbing':
    case 'partially_paid':
    case 'reversed':
      return 'warning';
    case 'ready':
      return 'success';
    default:
      return 'default';
  }
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface BillingEncounterRowProps {
  encounter: OrgBillingEncounterResponse;
  layout: TableLayoutMode;
  selected?: boolean;
  onSelectChange?: (encounterId: string, selected: boolean) => void;
  onCreateClaim?: (encounter: OrgBillingEncounterResponse) => void;
  onRowClick?: (encounter: OrgBillingEncounterResponse) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BillingEncounterRow: React.FC<BillingEncounterRowProps> = ({
  encounter,
  layout,
  selected = false,
  onSelectChange,
  onCreateClaim,
  onRowClick,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Actions menu state
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchor);

  const handleMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
  }, []);

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectChange?.(encounter.encounter_id, !selected);
  };

  // ---- Action handlers ----

  const handleViewNote = useCallback(() => {
    handleMenuClose();
    if (encounter.note_id) {
      navigate(`/note/${encounter.note_id}`);
    }
  }, [encounter.note_id, navigate, handleMenuClose]);

  const handleViewClaim = useCallback(() => {
    handleMenuClose();
    if (encounter.claim_id) {
      navigate(`/billing/claim/${encounter.claim_id}/edit`);
    }
  }, [encounter.claim_id, navigate, handleMenuClose]);

  const handleCreateClaim = useCallback(() => {
    handleMenuClose();
    onCreateClaim?.(encounter);
  }, [encounter, onCreateClaim, handleMenuClose]);

  const handleRowClick = useCallback(() => {
    onRowClick?.(encounter);
  }, [encounter, onRowClick]);

  // ---- Derived data ----

  const patientNameParts = useMemo(() => getPatientNameParts(encounter), [encounter]);
  const providerNameParts = useMemo(() => getNameParts(encounter.provider_name), [encounter.provider_name]);
  const displayName = useMemo(() => getPatientDisplayName(encounter), [encounter]);

  const hasPatient = Boolean(encounter.patient_id);
  const hasProvider = Boolean(encounter.provider_name);
  const dateTimeSource = encounter.scheduled_for ?? encounter.created_at;

  // Parse working billing claim
  const workingClaim = encounter.working_billing_claim as WorkingBillingClaim | null;
  const lineItems = workingClaim?.line_items ?? [];
  // Fallback to encounter-level CPT codes if no working claim
  const cptCodes = lineItems.length > 0
    ? lineItems.map(li => li.code_id).filter((c): c is string => Boolean(c))
    : (encounter.cpt_codes ?? []);
  const totalChargeCents = lineItems.reduce((sum, li) => sum + (li.charge_cents ?? 0), 0);

  const serviceType = (encounter.canonical_service_type as { name?: string } | null)?.name || null;

  const canCreateClaim = Boolean(!encounter.has_claim && encounter.patient_id && onCreateClaim);

  // Column visibility
  const showProvider = layout === 'full' || layout === 'standard';
  const showService = layout === 'full' || layout === 'standard';
  const showCptCharges = layout === 'full' || layout === 'standard';
  const showPayer = layout === 'full' || layout === 'standard';

  // Ready-to-bill tooltip content
const readyToBillTooltip = useMemo(() => {
    const reasons = encounter.ready_to_bill_reasons ?? [];
    const missing = encounter.info_missing ?? [];
    const parts: string[] = [];
    if (reasons.length > 0) {
      parts.push(...reasons);
    }
    if (missing.length > 0 && reasons.length > 0) {
      parts.push('', 'Missing info:');
      parts.push(...missing.map(item => `• ${item}`));
    } else if (missing.length > 0) {
      parts.push('Missing info:');
      parts.push(...missing.map(item => `• ${item}`));
    }
    if (parts.length === 0) {
      parts.push(getReadyToBillLabel(encounter.ready_to_bill));
    }
  return parts.join('\n');
}, [encounter.ready_to_bill_reasons, encounter.info_missing, encounter.ready_to_bill]);

  const firstReadyToBillReason = useMemo(() => {
    if (encounter.ready_to_bill === 'green') return undefined;

    const reasons = encounter.ready_to_bill_reasons ?? [];
    if (reasons.length > 0) return reasons[0];

    const missing = encounter.info_missing ?? [];
    if (missing.length > 0) return missing[0];

    return getReadyToBillLabel(encounter.ready_to_bill);
  }, [encounter.ready_to_bill, encounter.ready_to_bill_reasons, encounter.info_missing]);

  // Fee schedule display
  const feeScheduleTotal = encounter.fee_schedule_total_cents;

  // Build menu items
  const menuItems = useMemo(() => {
    const items: { label: string; icon: React.ReactNode; onClick: () => void; disabled?: boolean }[] = [];
    if (encounter.note_id) {
      items.push({ label: 'View Note', icon: <NoteIcon fontSize="small" />, onClick: handleViewNote });
    }
    if (encounter.has_claim && encounter.claim_id) {
      items.push({ label: 'View Claim', icon: <ViewClaimIcon fontSize="small" />, onClick: handleViewClaim });
    }
    if (canCreateClaim) {
      const isBillable = encounter.billable !== false;
      const hasWarning = isBillable && encounter.ready_to_bill && encounter.ready_to_bill !== 'green';
      items.push({
        label: !isBillable ? 'Create Claim (Note not signed)' : 'Create Claim',
        icon: !isBillable
          ? <WarningAmberIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          : hasWarning
          ? <WarningAmberIcon fontSize="small" sx={{ color: encounter.ready_to_bill === 'red' ? 'error.main' : 'warning.main' }} />
          : <CreateClaimIcon fontSize="small" />,
        onClick: handleCreateClaim,
        disabled: !isBillable,
      });
    }
    return items;
  }, [encounter.note_id, encounter.has_claim, encounter.claim_id, encounter.ready_to_bill, encounter.billable, canCreateClaim, handleViewNote, handleViewClaim, handleCreateClaim]);

  const hasActions = menuItems.length > 0;

  // ---- Actions menu (shared between layouts) ----

  const renderActionsMenu = () => (
    <Menu
      anchorEl={menuAnchor}
      open={menuOpen}
      onClose={handleMenuClose}
      onClick={e => e.stopPropagation()}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      slotProps={{
        paper: {
          sx: { minWidth: 160, boxShadow: theme.shadows[4] },
        },
      }}
    >
      {menuItems.map(item => (
        <MenuItem key={item.label} onClick={item.onClick} disabled={item.disabled} dense>
          <ListItemIcon sx={{ minWidth: '32px !important' }}>{item.icon}</ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.8rem' }}>{item.label}</ListItemText>
        </MenuItem>
      ))}
    </Menu>
  );

  // ---- Card layout (mobile) ----

  if (layout === 'card') {
    return (
      <Box
        onClick={handleRowClick}
        sx={{
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.grey[100]}`,
          cursor: onRowClick ? 'pointer' : 'default',
          transition: 'background-color 0.15s ease',
          bgcolor: selected ? `${theme.palette.primary.main}08` : 'transparent',
          '&:hover': onRowClick ? { bgcolor: selected ? `${theme.palette.primary.main}12` : theme.palette.grey[50] } : {},
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        {/* Top row: Checkbox + Icon + Date/Time + Status chips + Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          {onSelectChange && (
            <Checkbox
              checked={selected}
              onClick={handleCheckboxClick}
              size="small"
              sx={{ p: 0, flexShrink: 0 }}
            />
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: 28,
              borderRadius: 1,
              bgcolor: theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
              flexShrink: 0,
            }}
          >
            {getModalityIcon(encounter.modality)}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
              {formatDate(dateTimeSource)}
              {formatTime(dateTimeSource) && (
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary, ml: 0.5, fontSize: '0.75rem' }}
                >
                  {formatTime(dateTimeSource)}
                </Typography>
              )}
            </Typography>
          </Box>

          {encounter.note_id && (
            <Chip
              label={encounter.is_signed ? 'Signed' : 'Draft'}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 600,
                bgcolor: encounter.is_signed
                  ? `${theme.palette.success.main}15`
                  : `${theme.palette.grey[500]}15`,
                color: encounter.is_signed
                  ? theme.palette.success.main
                  : theme.palette.grey[600],
                '& .MuiChip-label': { px: 0.5 },
                flexShrink: 0,
              }}
            />
          )}

          {encounter.has_claim && (
            <Chip
              label={encounter.claim_status || 'Claim'}
              size="small"
              color={getClaimStatusColor(encounter.claim_status)}
              sx={{
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 600,
                textTransform: 'capitalize',
                '& .MuiChip-label': { px: 0.5 },
                flexShrink: 0,
              }}
            />
          )}

          {/* Ready to Bill */}
          <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{readyToBillTooltip}</Box>} arrow placement="top">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: getReadyToBillColor(encounter.ready_to_bill, theme),
                  flexShrink: 0,
                }}
              />
              {encounter.ready_to_bill !== 'green' && firstReadyToBillReason ? (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.15,
                    color: theme.palette.text.secondary,
                    fontSize: '0.6rem',
                    lineHeight: 1.1,
                    textAlign: 'center',
                    maxWidth: 72,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {firstReadyToBillReason}
                </Typography>
              ) : null}
            </Box>
          </Tooltip>

          {hasActions && (
            <IconButton size="small" onClick={handleMenuOpen} sx={{ p: 0.25, flexShrink: 0 }}>
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>

        {/* People row: Provider → Patient */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
          {hasProvider && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Avatar
                id={encounter.provider_name || 'unknown'}
                firstName={providerNameParts.firstName}
                lastName={providerNameParts.lastName}
                sx={{ width: 20, height: 20, fontSize: '0.6rem', fontWeight: 600, flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {encounter.provider_name}
              </Typography>
            </Box>
          )}

          {hasProvider && hasPatient && (
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.7rem' }}>→</Typography>
          )}

          {hasPatient && encounter.patient_id && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Avatar
                id={encounter.patient_id}
                firstName={patientNameParts.firstName}
                lastName={patientNameParts.lastName}
                sx={{ width: 20, height: 20, fontSize: '0.6rem', fontWeight: 600, flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {displayName}
                </Typography>
                {encounter.primary_payer_name && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary, fontSize: '0.6rem', lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                  >
                    {encounter.primary_payer_name}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Metadata row: service type + CPT codes + charges */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.3 }}>
          {serviceType && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
              {serviceType}
            </Typography>
          )}

          {cptCodes.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: 'fit-content', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 0.25, alignItems: 'center', flexWrap: 'wrap' }}>
                {cptCodes.slice(0, 3).map((code, idx) => (
                  <Chip
                    key={idx}
                    label={code}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.6rem',
                      fontFamily: 'monospace',
                      fontWeight: 600,
                      bgcolor: `${theme.palette.info.main}10`,
                      color: theme.palette.info.main,
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                ))}
                {cptCodes.length > 3 && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary, fontSize: '0.6rem', textAlign: 'center' }}
                  >
                    +{cptCodes.length - 3}
                  </Typography>
                )}
              </Box>
              {totalChargeCents > 0 ? (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    fontSize: '0.7rem',
                    textAlign: 'center',
                  }}
                >
                  {formatCents(totalChargeCents)}
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ visibility: 'hidden', fontSize: '0.7rem' }}>
                  $
                </Typography>
              )}
            </Box>
          ) : (
            totalChargeCents > 0 ? (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  fontSize: '0.7rem',
                }}
              >
                {formatCents(totalChargeCents)}
              </Typography>
            ) : null
          )}

          {feeScheduleTotal != null && feeScheduleTotal > 0 && (
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.65rem' }}>
              Fee: {formatCents(feeScheduleTotal)}
            </Typography>
          )}
        </Box>

        {renderActionsMenu()}
      </Box>
    );
  }

  // ---- Table-row layout (full / standard / compact) ----

  return (
    <Box
      onClick={handleRowClick}
      sx={{
        px: 2,
        py: 0.75,
        borderBottom: `1px solid ${theme.palette.grey[100]}`,
        cursor: onRowClick ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease',
        bgcolor: selected ? `${theme.palette.primary.main}08` : 'transparent',
        '&:hover': onRowClick ? { bgcolor: selected ? `${theme.palette.primary.main}12` : theme.palette.grey[50] } : {},
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Checkbox */}
        {onSelectChange && (
          <Checkbox
            checked={selected}
            onClick={handleCheckboxClick}
            size="small"
            sx={{ p: 0, flexShrink: 0 }}
          />
        )}

        {/* Grid row */}
        <Box
          sx={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: getGridTemplateColumns(layout),
            gap: GRID_GAP,
            alignItems: 'center',
            minHeight: 32,
          }}
        >
          {/* Encounter Icon */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
            }}
          >
            {getModalityIcon(encounter.modality)}
          </Box>

          {/* Date & Time */}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem', lineHeight: 1.3 }}>
              {formatDate(dateTimeSource)}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.65rem', lineHeight: 1 }}>
              {formatTime(dateTimeSource)}
            </Typography>
          </Box>

          {/* Provider */}
          {showProvider && (
            <Box sx={{ minWidth: 0 }}>
              {hasProvider ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Avatar
                    id={encounter.provider_name || 'unknown'}
                    firstName={providerNameParts.firstName}
                    lastName={providerNameParts.lastName}
                    sx={{ width: 24, height: 24, fontSize: '0.65rem', fontWeight: 600, flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={encounter.provider_name || undefined}
                  >
                    {encounter.provider_name}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.8rem' }}>—</Typography>
              )}
            </Box>
          )}

          {/* Patient */}
          <Box sx={{ minWidth: 0 }}>
            {hasPatient && encounter.patient_id ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Avatar
                  id={encounter.patient_id}
                  firstName={patientNameParts.firstName}
                  lastName={patientNameParts.lastName}
                  sx={{ width: 24, height: 24, fontSize: '0.65rem', fontWeight: 600, flexShrink: 0 }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={displayName}
                  >
                    {displayName}
                  </Typography>
                  {showPayer && encounter.primary_payer_name && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.6rem',
                        lineHeight: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                      }}
                      title={encounter.primary_payer_name}
                    >
                      {encounter.primary_payer_name}
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.8rem' }}>—</Typography>
            )}
          </Box>

          {/* Service Type */}
          {showService && (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{
                  fontSize: '0.8rem',
                  color: serviceType ? theme.palette.text.secondary : theme.palette.text.disabled,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={serviceType || undefined}
              >
                {serviceType || '—'}
              </Typography>
            </Box>
          )}

          {/* CPT Codes + Charges */}
          {showCptCharges && (
            <Box sx={{ minWidth: 0 }}>
              {cptCodes.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', gap: 0.25, flexWrap: 'wrap' }}>
                    {cptCodes.slice(0, 3).map((code, idx) => (
                      <Chip
                        key={idx}
                        label={code}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          bgcolor: `${theme.palette.info.main}10`,
                          color: theme.palette.info.main,
                          '& .MuiChip-label': { px: 0.5 },
                        }}
                      />
                    ))}
                    {cptCodes.length > 3 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.6rem',
                          lineHeight: '18px',
                          textAlign: 'center',
                        }}
                      >
                        +{cptCodes.length - 3}
                      </Typography>
                    )}
                  </Box>
                  {feeScheduleTotal != null && feeScheduleTotal > 0 ? (
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: '0.7rem',
                        lineHeight: 1,
                        textAlign: 'center',
                      }}
                    >
                      {formatCents(feeScheduleTotal)}
                    </Typography>
                  ) : totalChargeCents > 0 ? (
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        fontSize: '0.7rem',
                        lineHeight: 1,
                        textAlign: 'center',
                      }}
                    >
                      {formatCents(totalChargeCents)}
                    </Typography>
                  ) : null}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.8rem' }}>—</Typography>
              )}
            </Box>
          )}

          {/* Signed Status */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {encounter.note_id ? (
              <Chip
                label={encounter.is_signed ? 'Signed' : 'Draft'}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: encounter.is_signed
                    ? `${theme.palette.success.main}15`
                    : `${theme.palette.grey[500]}15`,
                  color: encounter.is_signed
                    ? theme.palette.success.main
                    : theme.palette.grey[600],
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.75rem' }}>—</Typography>
            )}
          </Box>

          {/* Claim Status */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {encounter.has_claim ? (
              <Chip
                label={encounter.claim_status || 'Claim'}
                size="small"
                color={getClaimStatusColor(encounter.claim_status)}
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  '& .MuiChip-label': { px: 0.5 },
                }}
              />
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.75rem' }}>
                —
              </Typography>
            )}
          </Box>

          {/* Ready to Bill */}
          <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{readyToBillTooltip}</Box>} arrow placement="top">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: getReadyToBillColor(encounter.ready_to_bill, theme),
                  cursor: 'default',
                }}
              />
              {encounter.ready_to_bill !== 'green' && firstReadyToBillReason ? (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.15,
                    color: theme.palette.text.secondary,
                    fontSize: '0.58rem',
                    lineHeight: 1.1,
                    textAlign: 'center',
                    maxWidth: 74,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {firstReadyToBillReason}
                </Typography>
              ) : null}
            </Box>
          </Tooltip>

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            {hasActions && (
              <IconButton size="small" onClick={handleMenuOpen} sx={{ p: 0.25 }}>
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>

      {renderActionsMenu()}
    </Box>
  );
};
